import { useState, useEffect, lazy, Suspense } from 'react';
import { useMapProvider } from './useMapProvider';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

const NLLeafletMap = lazy(() => import('@/components/maps/NLLeafletMap'));
const NLMapboxMap = lazy(() => import('@/components/maps/NLMapboxMap'));

export interface MapComponentProps {
  sites: any[];
  className?: string;
  getMarkerColor?: (site: any) => string;
  portalType?: 'crm' | 'charging';
  maintainZoom?: boolean;
  markerSize?: number;
  containerClassName?: string;
}

function MapLoader() {
  return (
    <div className="h-full w-full flex items-center justify-center bg-muted/50 rounded-lg border min-h-[200px]">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

// Stabiele wrapper component - BUITEN hooks gedefinieerd voor stabiele referentie
export function UnifiedMapComponent(props: MapComponentProps) {
  const { mapProvider, isLoading: isProviderLoading } = useMapProvider();
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[UnifiedMapComponent] Mount/Update - mapProvider:', mapProvider, 'isProviderLoading:', isProviderLoading);
    
    // Reset state when provider changes
    setError(null);
    
    if (isProviderLoading) {
      console.log('[UnifiedMapComponent] Waiting for provider to load...');
      return;
    }
    
    if (mapProvider !== 'mapbox') {
      console.log('[UnifiedMapComponent] Using Leaflet (provider is not mapbox)');
      setIsReady(true);
      return;
    }

    console.log('[UnifiedMapComponent] Provider is mapbox, fetching token...');

    // Timeout na 5 seconden - fallback naar Leaflet
    const timeout = setTimeout(() => {
      console.warn('[UnifiedMapComponent] Token fetch timeout after 5s - falling back to Leaflet');
      setMapboxToken(null);
      setError('Mapbox token fetch timeout');
      setIsReady(true);
    }, 5000);

    const fetchToken = async () => {
      try {
        console.log('[UnifiedMapComponent] Invoking get-mapbox-token edge function...');
        const { data, error: fetchError } = await supabase.functions.invoke('get-mapbox-token');
        
        clearTimeout(timeout);
        
        if (fetchError) {
          console.error('[UnifiedMapComponent] Token fetch error:', fetchError);
          setError(`Token fetch failed: ${fetchError.message}`);
          setMapboxToken(null);
          setIsReady(true);
          return;
        }
        
        if (!data?.token) {
          console.warn('[UnifiedMapComponent] No token in response data:', data);
          setError('No Mapbox token in response');
          setMapboxToken(null);
          setIsReady(true);
          return;
        }
        
        console.log('[UnifiedMapComponent] Mapbox token successfully fetched');
        setMapboxToken(data.token);
        setIsReady(true);
      } catch (err) {
        clearTimeout(timeout);
        console.error('[UnifiedMapComponent] Exception during token fetch:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setMapboxToken(null);
        setIsReady(true);
      }
    };
    
    fetchToken();

    return () => {
      clearTimeout(timeout);
    };
  }, [mapProvider, isProviderLoading]);

  // Log render state
  const shouldUseMapbox = mapProvider === 'mapbox' && !!mapboxToken;
  console.log('[UnifiedMapComponent] Render - isReady:', isReady, 'mapProvider:', mapProvider, 'hasToken:', !!mapboxToken, 'shouldUseMapbox:', shouldUseMapbox, 'error:', error, 'sites:', props.sites?.length);

  // Toon loader totdat we weten welke provider te gebruiken
  if (!isReady) {
    console.log('[UnifiedMapComponent] Rendering MapLoader (not ready)');
    return <MapLoader />;
  }

  if (shouldUseMapbox) {
    console.log('[UnifiedMapComponent] Rendering NLMapboxMap');
    return (
      <Suspense fallback={<MapLoader />}>
        <NLMapboxMap {...props} mapboxToken={mapboxToken!} />
      </Suspense>
    );
  }

  console.log('[UnifiedMapComponent] Rendering NLLeafletMap (fallback or default)');
  return (
    <Suspense fallback={<MapLoader />}>
      <NLLeafletMap {...props} />
    </Suspense>
  );
}

// Hook voor backwards compatibility
export function useMapWithProvider() {
  const { mapProvider } = useMapProvider();
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState(false);

  useEffect(() => {
    if (mapProvider !== 'mapbox') {
      setMapboxToken(null);
      return;
    }

    const fetchMapboxToken = async () => {
      setIsLoadingToken(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        if (error) throw error;
        if (data?.token) {
          setMapboxToken(data.token);
        }
      } catch (err) {
        console.error('[useMapWithProvider] Failed to fetch Mapbox token:', err);
      } finally {
        setIsLoadingToken(false);
      }
    };
    
    fetchMapboxToken();
  }, [mapProvider]);

  const shouldUseMapbox = mapProvider === 'mapbox' && !!mapboxToken;

  return {
    mapProvider,
    mapboxToken,
    isLoadingToken,
    shouldUseMapbox,
    MapComponent: UnifiedMapComponent, // Altijd dezelfde stabiele referentie
  };
}
